if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.sizeGuideTrigger = this.querySelector('#FittingModalBtn-btn-v2');
        this.quantityInput = this.querySelector('.quantity__input');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.fittingModal = document.querySelector('#FittingModal');
        this.sizeGuideCloseBtn = document.querySelector('.js-fitguideclose');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.refreshBulkAdd();

        this.fittingModal.addEventListener('click', (e) => {
          if (e.target.closest('.ProductTabs-fittingModal-content')) return;

          document.body.style.overflow = 'auto';
          this.fittingModal.classList.remove('is-active');
          this.fittingModal.classList.add('is-closed');
        });

        if (this.sizeGuideCloseBtn) {
          this.sizeGuideCloseBtn.addEventListener('click', (e) => {
            document.body.style.overflow = 'auto';
            this.fittingModal.classList.remove('is-active');
          });
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.addEventListener('manual-update', this.handleManualUpdate.bind(this));

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      refreshBulkAdd() {
        this.bulkAddBtn = this.querySelector('.js-product-upsell-cta-button');
        if (this.bulkAddBtn) {
          this.bulkAddBtn.addEventListener('click', this.handleBulkAddClick.bind(this));
        }
      }

      handleBulkAddClick(e) {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const qty = e.target.dataset.addQty ?? 1;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', currentVariantId);
        formData.append('quantity', qty);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bulk-add',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
          })
          .catch((error) => {
            console.error('Error adding to cart:', error);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            CartPerformance.measureFromEvent('add:user-action', e);
          });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      toggleSizeGuide(e) {
        const sizeGuideContainer = document.querySelector('.js-fittings-modal-content');

        const sizeChartTitle = document.querySelector('[data-size-chart-title]');
        const sizeGuideId = e.target.dataset.sizeGuideType;
        if (!sizeGuideId) return;
        this.fittingModal.classList.add('is-active');
        document.body.style.overflow = 'hidden';

        fetch('/pages/size-guides')
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const source = parser.parseFromString(html, 'text/html');
            const sourceElement = source.getElementById(sizeGuideId);

            if (!sourceElement) {
              console.error(`Size guide element with ID ${sizeGuideId} not found in response`);
              return;
            }

            sizeGuideContainer.innerHTML = sourceElement.innerHTML;
            sizeGuideContainer.querySelector('.size-guide__content').id = `Size-Guide-${this.sectionId}`;

            if (sizeChartTitle) {
              const sizeGuidePdpDrawerDropdownTitle = source.getElementById('size-guide-pdp-drawer-dropdown-title').value;
              sizeChartTitle.innerHTML = sizeGuidePdpDrawerDropdownTitle;
            }

            if (window.initializeFittingModalScrollbar) {
              console.log('initializeFittingModalScrollbar', window.initializeFittingModalScrollbar);
              requestAnimationFrame(() => window.initializeFittingModalScrollbar());
            }
          })
          .catch((error) => {
            console.error('Error loading size guide:', error);
          });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleManualUpdate(event) {
        const { target, selectedOptionValues } = event.detail.data;

        this.resetProductFormState();

        const productUrl = this.pendingRequestUrl || this.dataset.url;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, false),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl),
          shouldFocus: false,
        });

        this.querySelectorAll('product-swatches').forEach((el) => el.updateAvailabilityStyle());
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.url || target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });

        this.querySelectorAll('product-swatches').forEach((el) => el.updateAvailabilityStyle());
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, shouldFocus = true }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (!targetId || targetId == '' || !shouldFocus) return;
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (id === 'custom-swatches') {
              this.querySelector('variant-selects').classList.add('loading');
              const oldCheckbox = document.querySelector('.product-checkbox-bundle');
              const newCheckbox = html.querySelector('.product-checkbox-bundle');

              const oldSwatches = document.querySelector('custom-swatches');
              const newSwatches = html.querySelector('custom-swatches');

              if (oldCheckbox && !newCheckbox) {
                oldCheckbox.remove();
              } else if (!oldCheckbox && newCheckbox) {
                document
                  .querySelector('.quantity-buy-btn-container')
                  .insertAdjacentHTML('beforebegin', newCheckbox.outerHTML);
              } else if (oldCheckbox && newCheckbox) {
                if (oldSwatches && newSwatches && oldSwatches.dataset.collapsed && oldSwatches.dataset.collapsed == 'false') {
                  newCheckbox.classList.add('background');
                  newCheckbox.querySelector('input').checked = true;
                  newCheckbox.querySelector('.custom-purchase-option-checkbox').classList.add('custom-checkbox-check');
                }

                oldCheckbox.replaceWith(newCheckbox);
              }

              if (oldSwatches && !newSwatches) {
                oldSwatches.remove();
              } else if (!oldSwatches && newSwatches) {
                document.querySelector('.custom-swatches-container').appendChild(newSwatches);
              } else if (oldSwatches && newSwatches) {
                if (oldSwatches.dataset.collapsed && oldSwatches.dataset.collapsed == 'false') {
                  newSwatches.setAttribute('data-fetch-rendered', 'true');
                }
                oldSwatches.replaceWith(newSwatches);
              }

              return;
            }

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('price-desktop');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('custom-swatches');
          updateSourceFromDestination('pack-colors');
          updateSourceFromDestination('quantity-input');
          updateSourceFromDestination('quantity-select-container');
          updateSourceFromDestination('bis-trigger-container');

          const oldPresaleMessage = document.querySelector('#final-sale');
          const newPresaleMessage = html.querySelector('#final-sale');

          const finalSaleInput = document.querySelector('product-form input[name="properties[Heads Up]"]');
          if (finalSaleInput && newPresaleMessage) {
            finalSaleInput.value = 'Final sale. No returns or exchanges.';
          } else if (finalSaleInput && !newPresaleMessage) {
            finalSaleInput.value = null;
          }

          if (oldPresaleMessage && newPresaleMessage) {
            oldPresaleMessage.replaceWith(newPresaleMessage);
          } else if (!oldPresaleMessage && newPresaleMessage) {
            document.querySelector('variant-selects').insertAdjacentHTML('afterend', newPresaleMessage.outerHTML);
          } else if (oldPresaleMessage && !newPresaleMessage) {
            oldPresaleMessage.remove();
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const buttonInNewHtml = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const shouldDisable = buttonInNewHtml?.hasAttribute('disabled') ?? false;
          this.productForm?.toggleSubmitButton(shouldDisable, window.variantStrings.soldOut);

          const bisSelect = this.querySelector('select[name="bis_variant_id"]');
          if (bisSelect && variant?.id) {
            const optionToSelect = bisSelect.querySelector(`option[value="${variant.id}"]`);
            if (optionToSelect) {
              optionToSelect.selected = true;
              bisSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          // if(window.fbq) {
          //   window.fbq('track', 'ViewContent', {
          //     content_ids: [variant.id],
          //     content_name: variant.title,
          //     content_type: 'product_group',
          //     currency: 'USD',
          //     value: variant.price
          //   })
          // }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}

if (!customElements.get('size-guide-trigger')) {
  class SizeGuideTrigger extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.productInfo = document.querySelector('product-info');
      this.addEventListener('click', this.productInfo.toggleSizeGuide.bind(this.productInfo));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.productInfo.toggleSizeGuide.bind(this.productInfo));
    }
  }

  customElements.define('size-guide-trigger', SizeGuideTrigger);
}

if (!customElements.get('size-calculator')) {
  class SizeCalculator extends HTMLElement {
    constructor() {
      super();
      this.calculatorButton = this.querySelector('[data-calculator-get-size]');
    }

    connectedCallback() {
      this.calculatorButton.addEventListener('click', this._handleGetSizeClick.bind(this));
    }

    disconnectedCallback() {
      this.calculatorButton.removeEventListener('click', this._handleGetSizeClick.bind(this));
    }

    _handleGetSizeClick() {
      const firstOption = +this.querySelector('[data-calculator-first-option]').value;
      const secondOption = +this.querySelector('[data-calculator-second-option]').value;
      const size = calculateBraSize({ underbust: firstOption, bust: secondOption });
      const textsWrapperDesktop = this.querySelector('.ProductTabs-fittingModal-calculator-message.hidden-on-mobile');
      const textsWrapperMobile = this.querySelector('.ProductTabs-fittingModal-calculator-message.hidden-on-desktop');
      if (window.innerWidth < 750) {
        textsWrapperMobile.style.display = 'block';
        textsWrapperDesktop.style.display = 'none';
      } else {
        textsWrapperDesktop.style.display = 'block';
        textsWrapperMobile.style.display = 'none';
      }
      if (!size.found) {
        this.querySelectorAll('[data-calculator-message]').forEach(message => message.style.display = 'none');
        this.querySelectorAll('[data-calculator-not-found-message]').forEach(message => message.style.display = 'block');
        this.querySelectorAll('[data-calculator-size]').forEach(sizeElement => sizeElement.textContent = '');
        return;
      }
      this.querySelectorAll('[data-calculator-message]').forEach(message => message.style.display = 'block');
      this.querySelectorAll('[data-calculator-not-found-message]').forEach(message => message.style.display = 'none');
      this.querySelectorAll('[data-calculator-size]').forEach(sizeElement => sizeElement.textContent = size.size);
    }
  }

  customElements.define('size-calculator', SizeCalculator);
}

function calculateBraSize({ underbust, bust }) {
  if (typeof underbust !== 'number' || typeof bust !== 'number') {
    throw new TypeError('Selected measurements must be numbers');
  }

  let uIn = underbust;
  const bIn = bust;

  if (uIn % 2 === 0) {
    uIn = uIn + 4;
  } else {
    uIn = uIn + 5;
  }

  if (uIn <= 0 || bIn <= 0) {
    throw new RangeError('Selected measurements must be positive numbers');
  }

  const sizes = {
    'XS': { 'measurement': ['30-30', '30-31', '32-32'], 'answerSize': 'Extra Small (XS)' },
    'S': { 'measurement': ['30-32', '30-33', '32-33', '32-34', '34-34'], 'answerSize': 'Small (S)' },
    'M': { 'measurement': ['30-34', '32-35', '34-35', '34-36', '36-36', '36-37', '38-38'], 'answerSize': 'Medium (M)' },
    'L': { 'measurement': ['32-36', '32-37', '34-37', '34-38', '36-38', '36-39', '38-39', '38-40', '40-40', '40-41', '42-42'], 'answerSize': 'Large (L)' },
    'XL': { 'measurement': ['34-39', '36-40', '36-41', '38-41', '38-42', '40-42', '40-43', '42-43', '42-44', '44-45'], 'answerSize': 'Extra Large (XL)' },
    '2XL': { 'measurement': ['38-43', '40-44', '40-45', '42-45', '42-46', '44-46', '44-47', '46-48'], 'answerSize': '2XL' },
    '3XL': { 'measurement': ['42-47', '44-48', '46-49', '48-50'], 'answerSize': '3XL' },
    '4XL': { 'measurement': ['44-49', '46-50', '48-51', '50-52'], 'answerSize': '4XL' },
    '5XL': { 'measurement': ['46-51', '48-52'], 'answerSize': '5XL' },
    '6XL': { 'measurement': ['48-53'], 'answerSize': '6XL' }
  };

  let cup = `${uIn}-${bIn}`;

  let size = Object.keys(sizes).find(sz => sizes[sz].measurement.includes(cup));
  size = sizes[size]?.answerSize;
  const found = Boolean(size);
  if (!found) {
    size = `${window.calculatorMessageNotFound || 'Not found'}`;
  }

  window.dataLayer =  window.dataLayer || [];             
  window.dataLayer.push({
      'event': 'sizeCalculatorResult',
      'event_label': `${size}`,
      'event_category': `${underbust}`,
      'event_action': `${bust}`
  });

  return { size, found };
}